import { inject, injectable } from "@launchtray/tsyringe-async";
import { ModuleFlowExecutor } from "by/gamefactory/cityversetycoon/composition/module/ModuleFlowExecutor";
import { LatestNewsModule } from "./LatestNewsModule";
import { AsyncOperationNotification } from "../../common/components/AsyncOperationNotification";
import { SceneManager } from "by/gamefactory/scene/SceneManager";
import { ILogger } from "by/gamefactory/cityversetycoon/logger/ILogger";
import { NoCacheDynamicPackage } from "../../common/resources/NoCacheDynamicPackage";
import { ILatestNewsModel } from "../../common/models/latestnews/ILatestNewsModel";
import { LatestNewsPopupScene } from "./scenes/LatestNewsPopupScene";
import { DynamicResourcesPackage } from "resources/DynamicResourcesPackage";
import { CancellationToken } from "async/CancellationToken";
import { CancellationTokenSource } from "async/CancellationTokenSource";
import { IPlayerModel } from "../../common/models/IPlayerModel";
import { IConnectionModel } from "by/gamefactory/cityversetycoon/networking/IConnectionModel";
import { nameof } from "bindable-data";
import { IContextResolver } from "by/gamefactory/cityversetycoon/composition/IContextResolver";
import { MyWalletModule } from "../myWallet/MyWalletModule";
import { HourlyBonusModule } from "../hourlyBonus/HourlyBonusModule";
import { HourlyBonusType } from "../hourlyBonus/HourlyBonusExecutionOptions";
import { HourlyBonusState } from "../hourlyBonus/mainHourlyBonus/HourlyBonusState";
import { LatestNewsPopupSceneResult, LatestNewsResultOptions } from "./LatestNewsResultOptions";
import { Web3Model } from "../../../../web3/models/Web3Model";

type LatestNewsPopupSceneFactory = (newsDynamicResources: DynamicResourcesPackage, cancellationToken: CancellationToken) => Promise<LatestNewsPopupScene>;

@injectable()
export class LatestNewsModuleFlowExecutor extends ModuleFlowExecutor<LatestNewsModule> {
    private static readonly NO_CACHE_TAG = "latest_news";

    private readonly sceneManager: SceneManager;

    private readonly latestNewsModel: ILatestNewsModel;
    private readonly playerModel: IPlayerModel;
    private readonly connectionModel: IConnectionModel;
    private readonly web3Model: Web3Model;
    private readonly asyncOperationNotification: AsyncOperationNotification;
    private readonly logger: ILogger;
    private readonly contextResolver: IContextResolver;

    private readonly newsDynamicResources: NoCacheDynamicPackage;
    private readonly cancellationTokenSource: CancellationTokenSource;

    public constructor(
        sceneManager: SceneManager,
        @inject("ILatestNewsModel") latestNewsModel: ILatestNewsModel,
        @inject("IPlayerModel") playerModel: IPlayerModel,
        @inject("IConnectionModel") connectionModel: IConnectionModel,
        web3Model: Web3Model,
        asyncOperationNotification: AsyncOperationNotification,
        @inject("ILogger") logger: ILogger,
        @inject("IContextResolver") contextResolver: IContextResolver
    ) {
        super();
        this.sceneManager = sceneManager;
        this.latestNewsModel = latestNewsModel;
        this.playerModel = playerModel;
        this.connectionModel = connectionModel;
        this.web3Model = web3Model;
        this.asyncOperationNotification = asyncOperationNotification;
        this.logger = logger;
        this.contextResolver = contextResolver;
        this.newsDynamicResources = new NoCacheDynamicPackage(LatestNewsModuleFlowExecutor.NO_CACHE_TAG);
        this.cancellationTokenSource = new CancellationTokenSource();
        this.connectionModel.propertyChanged.add(this.onConnectionModelPropertyChanged, this);
    }

    public override async execute(module: LatestNewsModule): Promise<void> {
        const token = this.cancellationTokenSource.token;
        try {
            this.asyncOperationNotification.show();
            await this.latestNewsModel.getLatestNews(true);
            await Promise.all([
                this.latestNewsModel.getLatestNewsStatus(),
                this.playerModel.loadPlayerData(),
                this.web3Model.waitSmartWalletConnection()
            ]);
            if (token.isCancellationRequested) {
                this.asyncOperationNotification.hide();
                return;
            }
            await this.loadDynamicResources();

            const latestNewsFactory: LatestNewsPopupSceneFactory = await module.resolve("LatestNewsPopupSceneFactory");
            const scene = await latestNewsFactory(this.newsDynamicResources, token);
            this.asyncOperationNotification.hide();

            if (token.isCancellationRequested) {
                if (this.newsDynamicResources.loaded) {
                    this.newsDynamicResources.destroy();
                }
                scene.view?.destroy();
                return;
            }

            const result: LatestNewsResultOptions<LatestNewsPopupSceneResult> = await this.sceneManager.showViewableAsync(scene);
            if (result.type === LatestNewsPopupSceneResult.MY_WALLET) {
                const myWalletModule = await this.asyncOperationNotification.autoShowHide(this.contextResolver.resolve(MyWalletModule));
                await myWalletModule.start((result as LatestNewsResultOptions<LatestNewsPopupSceneResult.MY_WALLET>).args);
            }
            if (result.type === LatestNewsPopupSceneResult.GOLDEN_WHEEL) {
                const module = await this.asyncOperationNotification.autoShowHide(this.contextResolver.resolve(HourlyBonusModule));
                await module.start({ type: HourlyBonusType.MAIN, args: HourlyBonusState.DEFAULT });
            }
        }
        catch (e: unknown) {
            this.asyncOperationNotification.hide();
            this.logger.error(`Exception on executing LatestNewsFlow: `, e);

        } finally {
            this.cancellationTokenSource.dispose();
            this.connectionModel.propertyChanged.remove(this.onConnectionModelPropertyChanged, this);
            if (this.newsDynamicResources.loaded) {
                this.newsDynamicResources.destroy();
            }
        }
    }

    private async loadDynamicResources(): Promise<void> {
        const latestNews = this.latestNewsModel.latestNews.value?.items;
        if (latestNews == null || latestNews.length === 0) {
            return;
        }
        let assetsToLoad = 0;
        for (const item of latestNews) {
            if (item.guest != null) {
                this.newsDynamicResources.add(item.guest.avatar);
                assetsToLoad++;
            }
        }
        if (assetsToLoad > 0) {
            await this.newsDynamicResources.load();
        }
    }

    private onConnectionModelPropertyChanged(model: IConnectionModel, key: string): void {
        switch (key) {
            case nameof(model, "connected"):
                if (!model.connected.value) {
                    this.logger.debug("LatestNewsModule cancellation requested, reason: disconnect");
                    this.cancellationTokenSource.cancel();
                }
                break;
        }
    }
}